import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ShieldCheck, KeyRound, Copy, Download, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsMfa = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [newCodes, setNewCodes] = useState(null);

  const loadStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/mfa/status`);
      setStatus(data);
    } catch (e) {
      toast.error("Could not load MFA status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/mfa/regenerate-codes`, {});
      setNewCodes(data.backup_codes);
      toast.success("New backup codes generated");
      loadStatus();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "step_up_required") {
        toast.error("For security, please sign out and back in, then try again.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not regenerate codes");
      }
    } finally {
      setRegenerating(false);
    }
  };

  const downloadCodes = () => {
    const blob = new Blob(
      [`CPC Pay — Backup Codes\nEach code works once.\n\n${newCodes.join("\n")}\n`],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "cpd-portal-backup-codes.txt"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="max-w-2xl space-y-6" data-testid="settings-mfa-page">
        <div>
          <h1 className="text-3xl font-bold text-emerald-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Security
          </h1>
          <p className="text-stone-600 mt-1">Manage your two-factor authentication.</p>
        </div>

        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <ShieldCheck className="w-5 h-5 text-emerald-700" /> Two-factor authentication
            </CardTitle>
            <CardDescription>An authenticator app code is required every time you sign in.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <p className="text-stone-500 text-sm">Loading…</p>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-stone-600">Status</span>
                  <Badge className="bg-emerald-100 text-emerald-800" data-testid="mfa-status-badge">
                    {status?.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-stone-600">Backup codes remaining</span>
                  <span className="font-semibold" data-testid="mfa-codes-remaining">{status?.backup_codes_remaining ?? 0}</span>
                </div>
                {status?.backup_codes_low && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> You're low on backup codes. Generate a new set.
                  </div>
                )}
                {!status?.email_fallback_available && (
                  <p className="text-xs text-stone-400">
                    Email code fallback is not configured. Use your authenticator app or backup codes.
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <KeyRound className="w-5 h-5 text-emerald-700" /> Backup codes
            </CardTitle>
            <CardDescription>Regenerating invalidates all previous codes immediately.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {newCodes && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 bg-stone-100 rounded-xl p-4" data-testid="new-backup-codes">
                  {newCodes.map((c) => (
                    <code key={c} className="text-center font-mono text-sm py-1.5 bg-white rounded-md border border-stone-200">{c}</code>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={downloadCodes}>
                    <Download className="w-4 h-4 mr-2" /> Download
                  </Button>
                  <Button type="button" variant="outline" className="flex-1"
                    onClick={() => { navigator.clipboard.writeText(newCodes.join("\n")); toast.success("Copied"); }}>
                    <Copy className="w-4 h-4 mr-2" /> Copy all
                  </Button>
                </div>
              </div>
            )}
            <Button onClick={regenerate} disabled={regenerating} className="bg-emerald-900 hover:bg-emerald-800 rounded-full"
              data-testid="regenerate-codes-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${regenerating ? "animate-spin" : ""}`} />
              {regenerating ? "Generating…" : "Generate new backup codes"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SettingsMfa;
